import { useState, useEffect, useMemo } from 'react';

export const useReturnItemsProcessor = (fgData) => {
  const [approvedReturnItems, setApprovedReturnItems] = useState([]);

  // Build a list of items in the fulfillment group that are approved for return
  useEffect(() => {
    if (!fgData) return;
    
    // Debug order items to help diagnose the issue
    console.log('FG Order Items:', fgData?.orderItems?.map(item => ({
      id: item.id,
      status: item.status,
      returnRequest: !!item.returnRequest,
      returnRequestId: item.returnRequest?.id
    })));
    
    // Debug return requests if available
    if (fgData?.returnRequests) {
      console.log('Return Requests:', fgData.returnRequests.map(req => ({
        id: req.id,
        status: req.status,
        orderItemIds: req.orderItems ? req.orderItems.map(item => typeof item === 'object' ? item.id : item) : []
      })));
    }
    
    // Get all requests - use both returnRequests and requests arrays for backward compatibility
    const allRequests = [
      ...(fgData.returnRequests || []),
      ...(fgData.requests || [])
    ];
    
    // Get all item IDs associated with return requests - items explicitly linked in the database
    const itemIdsWithRequests = new Set();
    
    // Process all requests to extract orderItems
    allRequests.forEach(req => {
      // For returnRequests or item_return type requests
      if ((req.requestType === 'item_return' || req.requestType === undefined) && req.orderItems && req.orderItems.length > 0) {
        req.orderItems.forEach(item => {
          // Item could be an object or just an ID
          itemIdsWithRequests.add(typeof item === 'object' ? item.id : item);
        });
      }
      
      // Also check for relatedOrderItemComponentId (single item requests)
      if (req.relatedOrderItemComponentId) {
        itemIdsWithRequests.add(req.relatedOrderItemComponentId);
      }
    });
    
    console.log('Item IDs linked to requests:', Array.from(itemIdsWithRequests));
    
    // If we found explicit links, use them to filter
    if (itemIdsWithRequests.size > 0) {
      const explicitlyLinkedItems = fgData.orderItems.filter(item => 
        itemIdsWithRequests.has(item.id) || 
        itemIdsWithRequests.has(item.id.toString())
      );
      
      console.log('Explicitly linked items:', explicitlyLinkedItems.map(i => i.id));
      
      if (explicitlyLinkedItems.length > 0) {
        setApprovedReturnItems(explicitlyLinkedItems);
        return;
      }
    }
    
    // Fallback: look for items with returnRequest property or return statuses
    const itemsWithReturnProps = fgData.orderItems.filter(item => 
      item.returnRequest || 
      ['return_approved_by_merchant', 'return_requested_by_customer'].includes(item.status)
    );
    
    console.log('Items with return properties:', itemsWithReturnProps.map(i => i.id));
    
    if (itemsWithReturnProps.length > 0) {
      setApprovedReturnItems(itemsWithReturnProps);
      return;
    }
    
    // Last resort: if FG status indicates a return, include all items
    if (['return_approved', 'return_shipping_label_created'].includes(fgData.status)) {
      console.log('FG has return status, including all items');
      setApprovedReturnItems(fgData.orderItems || []);
      return;
    }
    
    setApprovedReturnItems([]);
  }, [fgData]);

  // Extract return reason from the first item
  const groupReturnReason = useMemo(() => {
    if (!approvedReturnItems || approvedReturnItems.length === 0) return null;
    const rr = approvedReturnItems[0].returnRequest;
    if (!rr) return null;
    
    const extract = details => {
      if (!details) return undefined;
      if (typeof details === 'string') {
        try { return JSON.parse(details).reason; } catch { return undefined; }
      }
      return details.reason;
    };
    
    const raw = rr.reason || extract(rr.requestDetails) || 'Not specified';
    return raw.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase());
  }, [approvedReturnItems]);

  // Check if all items already have return labels
  const allHaveLabels = useMemo(() => {
    // If FG status is return_shipped, it means labels have been created
    const fgHasShippedStatus = fgData?.status === 'return_shipped';
    
    // Check individual items for shipping labels
    const itemsHaveLabels = approvedReturnItems?.length > 0 && 
      approvedReturnItems.every(it => it.returnRequest?.shippingLabelUrl);
    
    // Also check for group-level return deliveries
    const hasReturnDeliveries = fgData?.returnDeliveries?.length > 0 && 
      fgData.returnDeliveries.some(delivery => delivery.shippingLabelUrl);
    
    const hasLabels = fgHasShippedStatus || itemsHaveLabels || hasReturnDeliveries;
    
    console.log('[useReturnItemsProcessor] allHaveLabels check:', {
      approvedReturnItemsLength: approvedReturnItems?.length || 0,
      fgStatus: fgData?.status,
      fgHasShippedStatus,
      itemsHaveLabels,
      hasReturnDeliveries,
      hasLabels,
      returnDeliveries: fgData?.returnDeliveries?.map(d => ({
        id: d.id,
        hasShippingLabelUrl: !!d.shippingLabelUrl,
        trackingNumber: d.tracking_number
      })) || [],
      itemsWithLabels: approvedReturnItems?.map(item => ({
        id: item.id,
        hasShippingLabelUrl: !!item.returnRequest?.shippingLabelUrl,
        shippingLabelUrl: item.returnRequest?.shippingLabelUrl
      })) || []
    });
    
    return hasLabels;
  }, [approvedReturnItems, fgData]);

  // Get existing label data from return deliveries if labels already exist
  const existingLabelData = useMemo(() => {
    if (!allHaveLabels) return null;
    
    // First check return deliveries for group-level labels
    if (fgData?.returnDeliveries?.length > 0) {
      const delivery = fgData.returnDeliveries.find(d => d.shippingLabelUrl);
      if (delivery) {
        return {
          success: true,
          labelUrl: delivery.shippingLabelUrl,
          trackingNumber: delivery.tracking_number,
          carrier: delivery.carrier,
          // Additional fields that might be needed
          label_url: delivery.shippingLabelUrl,
          tracking_number: delivery.tracking_number
        };
      }
    }
    
    // Fallback to item-level labels
    if (approvedReturnItems?.length > 0) {
      const itemWithLabel = approvedReturnItems.find(item => item.returnRequest?.shippingLabelUrl);
      if (itemWithLabel && itemWithLabel.returnRequest) {
        return {
          success: true,
          labelUrl: itemWithLabel.returnRequest.shippingLabelUrl,
          trackingNumber: itemWithLabel.returnRequest.trackingNumber,
          carrier: itemWithLabel.returnRequest.carrier,
          // Additional fields that might be needed
          label_url: itemWithLabel.returnRequest.shippingLabelUrl,
          tracking_number: itemWithLabel.returnRequest.trackingNumber
        };
      }
    }
    
    return null;
  }, [allHaveLabels, fgData, approvedReturnItems]);

  return {
    approvedReturnItems,
    setApprovedReturnItems,
    groupReturnReason,
    allHaveLabels,
    existingLabelData
  };
}; 